import React, { useState, useEffect } from 'react';
import { db } from '../config/firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { initializeCronJobs } from '../utils/cronJobs/initializeCronJobs';

// Mapa de nomes das tarefas
const taskNames: { [key: string]: string } = {
  'stock-update': 'Atualizar Stock das Encomendas',
  'woocommerce-import': 'Importar Pedidos WooCommerce',
  'new-messages': 'Verificar Novas Mensagens',
  'quote-requests': 'Verificar Pedidos de Orçamento',
  'new-orders': 'Verificar Novos Pedidos',
  'pending-payments': 'Verificar Pagamentos Pendentes',
  'sales-goals': 'Verificar Metas de Vendas',
  'low-stock': 'Verificar Produtos com Estoque Baixo',
  'unprocessed-sales': 'Verificar Vendas Não Processadas',
  'sync-woocommerce-orders': 'Sincronizar Pedidos WooCommerce',
  'sync-baselinker': 'Sincronizar com BaseLinker',
  'campaign-payments': 'Verificar Pagamentos de Campanhas'
};

export default function TaskMonitor() {
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reinicializando, setReinicializando] = useState(false);
  const [progressInfo, setProgressInfo] = useState<{ current: number; total: number; jobName: string } | null>(null);

  // Função para carregar tarefas
  const loadTasks = async () => {
    try {
      setLoading(true);
      const tasksRef = collection(db, 'cronJobs');
      const q = query(tasksRef, orderBy('lastRun', 'desc'), limit(10));
      const snapshot = await getDocs(q);
      
      const tasksList = snapshot.docs.map(doc => {
        const data = doc.data();
        const taskId = data.taskId || doc.id; // Usa o taskId se disponível, senão usa o doc.id
        return {
          id: doc.id,
          name: taskNames[taskId] || taskId,
          status: data.status || 'pending',
          lastRun: data.lastRun?.toDate() || null,
          nextRun: data.nextRun?.toDate() || null,
          error: data.error
        };
      });

      setTasks(tasksList);
      setError(null);
    } catch (err) {
      console.error('Erro ao carregar tarefas:', err);
      setError('Erro ao carregar tarefas. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Efeito para carregar tarefas inicialmente e a cada 30 segundos
  useEffect(() => {
    loadTasks();
    const interval = setInterval(loadTasks, 30000);
    return () => clearInterval(interval);
  }, []);

  // Função para reinicializar as tarefas
  const handleReinitialize = async () => {
    try {
      setReinicializando(true);
      await initializeCronJobs((jobName, current, total) => {
        setProgressInfo({ jobName, current, total });
      });
      await loadTasks();
    } catch (err) {
      console.error('Erro ao reinicializar tarefas:', err);
      setError('Erro ao reinicializar tarefas. Por favor, tente novamente.');
    } finally {
      setReinicializando(false);
      setProgressInfo(null);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Monitor de Tarefas</h1>
        <button
          onClick={handleReinitialize}
          disabled={reinicializando}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {reinicializando ? 'Reinicializando...' : 'Reinicializar Tarefas'}
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {progressInfo && (
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded mb-4">
          Inicializando {progressInfo.jobName} ({progressInfo.current}/{progressInfo.total})
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">Carregando...</div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  TAREFA
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  STATUS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ÚLTIMA EXECUÇÃO
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  PRÓXIMA EXECUÇÃO
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tasks.map((task) => (
                <tr key={task.id}>
                  <td className="px-6 py-4 whitespace-nowrap">{task.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        task.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : task.status === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : task.status === 'running'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {task.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {task.lastRun ? task.lastRun.toLocaleString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {task.nextRun ? task.nextRun.toLocaleString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
